import {
  AppErrorCode,
  Context,
  CoreDict,
  Id,
  Logger,
  TranslationService,
  createObservableValue,
  createOptionalObservableValue,
  enumMapToSame,
  isAppError,
  isTrCore,
  throwAccessDenied,
  throwInternalError,
} from '../../../common'
import { Role, RolesService, User, UsersService } from '../../users'
import { AuthError, AppAuthService, UserPermissions, LoginType, AuthRequest } from './appAuthService'
import { Permissions } from './permissions'
import { Api, AuthApi, HttpResponse } from '../../../api'
import { AuthHttpClient } from '../data/authHttpClient'
import { DBConnection, ROTransaction, runWithROTransaction, runWithRWTransaction } from '../../../db'

export interface RemoteAuthSession {
  token: string
  userId: Id
  validTo: Date
}

export interface RemoteAuthSessionRepository {
  get(tx: ROTransaction): Promise<RemoteAuthSession | undefined>
  set(tx: ROTransaction, session: RemoteAuthSession | undefined): Promise<void>
}

export class RemoteAppAuthService implements AppAuthService {
  private api: AuthApi
  isInitialized = createObservableValue<boolean, Context>(false)
  authorizedUser = createOptionalObservableValue<User | undefined, Context>()
  role = createOptionalObservableValue<Role | undefined, Context>()
  permissions = createObservableValue<UserPermissions, Context>({ rolePermissions: [], superUser: false })
  authorizationRequest = createOptionalObservableValue<AuthRequest, Context>()

  private logger = new Logger('RemoteAppAuthService')

  constructor(
    private opts: {
      dbConnection: DBConnection
      httpClient: AuthHttpClient
      usersService: UsersService
      rolesService: RolesService
      anonymousPermissions?: UserPermissions
      translationService: TranslationService<CoreDict>
      remoteAuthSessionRepository: RemoteAuthSessionRepository
      checksAfterLogin?: (ctx: Context) => Promise<void>
    },
  ) {
    this.api = new AuthApi(opts.httpClient, undefined, '')
    opts.httpClient.addMiddleware({ handleResponse: r => this.handleResponse(r) })
  }

  private async handleResponse(response: HttpResponse) {
    if (response.status !== 401) return

    this.logger.warn({}, '<6652dad2> Authorization needed response. Reset authorization')

    await runWithRWTransaction(this.opts.dbConnection, tx => this.opts.remoteAuthSessionRepository.set(tx, undefined))
    await this.sessionUpdated({}, undefined)
  }

  private checkInitialization(ctx: Context) {
    if (!this.isInitialized) {
      throwInternalError(
        `<b6b8abc5> ${this.opts.translationService.translate(
          ctx.langCode,
          d => d.tools.serviceWasNotInitializedError,
        )}`,
      )
    }
  }

  async initialize(ctx: Context) {
    if (this.isInitialized.value) return
    this.logger.debug(ctx, '<b1da185c> Loading auth session')
    const session = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.remoteAuthSessionRepository.get(tx),
    )
    await this.sessionUpdated(ctx, session)
    this.isInitialized.setValue(true, ctx)
  }

  private async sessionUpdated(ctx: Context, session: RemoteAuthSession | undefined) {
    if (session) {
      this.opts.httpClient.authBearerToken = async () => session.token

      const user =
        (await this.opts.usersService.findByIdNonDeleted(ctx, session.userId)) ??
        throwInternalError(
          `<6dea821c> ${this.opts.translationService.translate(ctx, d => d.users.notFoundByIdError, session.userId)}`,
        )
      const role = await this.opts.rolesService.getById(ctx, user.roleId)

      const permissions: UserPermissions = {
        rolePermissions: role.permissions,
        superUser: role.permissions.includes(Permissions.SUPERUSER.name),
      }
      this.role.setValue(role, ctx)
      this.authorizedUser.setValue(user, ctx)
      this.permissions.setValue(permissions, ctx)
    } else {
      this.opts.httpClient.authBearerToken = undefined
      this.authorizedUser.setValue(undefined, ctx)
      this.permissions.setValue(this.opts.anonymousPermissions ?? { rolePermissions: [], superUser: false }, ctx)
    }
  }

  private async loginByImpl(
    ctx: Context,
    block: () => Promise<Api.AuthSuccessResponse>,
  ): Promise<AuthError | undefined> {
    let r: Api.AuthSuccessResponse
    try {
      r = await block()
    } catch (e) {
      if (isAppError(e)) {
        if (e.code === AppErrorCode.ACCESS_DENIED) {
          if (isTrCore(e.messageWithoutUid)) {
            throwInternalError('<a99c98b1>')
          }
          return { message: e.messageWithoutUid }
        }
      }
      throw e
    }

    const remoteSession: RemoteAuthSession = {
      token: r.bearerToken,
      userId: r.userId,
      validTo: new Date(r.expiredAt),
    }

    await runWithRWTransaction(this.opts.dbConnection, tx =>
      this.opts.remoteAuthSessionRepository.set(tx, remoteSession),
    )

    await this.sessionUpdated(ctx, remoteSession)

    if (this.opts.checksAfterLogin) {
      try {
        await this.opts.checksAfterLogin(ctx)
      } catch (e) {
        await this.logout(ctx)
        throw e
      }
    }

    return undefined
  }

  async login(ctx: Context, email: string, password: string): Promise<AuthError | undefined> {
    this.checkInitialization(ctx)
    return this.loginByImpl(ctx, async () => {
      return (await this.api.authLogin(ctx, { email, password })).body
    })
  }

  async logout(ctx: Context): Promise<void> {
    this.checkInitialization(ctx)

    if (!this.authorizedUser.value) {
      throwAccessDenied(this.opts.translationService.translate(ctx, d => d.authorization.youAreNotAuthorized))
    }
    try {
      await this.api.authLogout(ctx)
    } catch (e) {
      this.logger.error(ctx, '<dbff90ab> Logout request error', { error: e })
    }

    await runWithRWTransaction(this.opts.dbConnection, tx => this.opts.remoteAuthSessionRepository.set(tx, undefined))
    await this.sessionUpdated(ctx, undefined)
  }

  async loginBy(ctx: Context, type: LoginType, email: string, answer: string): Promise<AuthError | undefined> {
    this.checkInitialization(ctx)

    return this.loginByImpl(ctx, async () => {
      return (await this.api.authLoginBy(ctx, { loginType: enumMapToSame(type), email, answer })).body
    })
  }

  async getControlQuestion(ctx: Context, email: string): Promise<string | undefined> {
    this.checkInitialization(ctx)

    const result = await this.api.authGetControlQuestion(ctx, email)
    return result.body.controlQuestion
  }
}
